import {
  ComparatorContext,
  ComponentDescriptor,
  FieldHandler,
  FormField,
  IProduct,
  LegacyOrderDefinition,
  SetFormFieldsEvent,
  Step,
  UseWizardOptions,
  WizardInterpreter,
  getStorage,
  legacyOrderDefinitionToSteps,
  parseLegacyComponents,
  replaceWithPreviousStepsValues,
  transformers,
  useCurrentStepIndex,
  useField,
  useFields,
  useFieldsAttribute,
  useFormAttribute,
  useSteps,
  useStorage,
  useWizardService,
  validators,
} from '@kizi/comparator-core';
import { Button, Div, DivProps, Heading } from '@kizi/core-components';
import { Row, RowProps, TabDefinition } from '@kizi/shared-components';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Field, Spinner } from '../..';
import { parseProductFieldData } from '../../../utils/product';
import {
  ApplyPageLayout,
  ApplyPageLayoutCar,
} from '../../ApplyLayouts/ApplyPageLayout';
import { Banner } from '../../Banner';
import { TabViewWizard, TabViewWizardProps } from '../../TabViewWizard';

export interface ApplyPageWizardProps
  extends Omit<
    TabViewWizardProps,
    'tabs' | 'steps' | 'children' | 'wizardOptions'
  > {
  tabs: (Omit<TabDefinition, 'content'> & { content: ComponentDescriptor })[];
  fieldNames: {
    selectedProduct: string;
    wizardSteps: string;
  };
  productType: 'cars' | 'energy';
  injectedFields?: string[];
  comparatorType?: string;
}

export interface MembershipWarningProps extends DivProps {}

export const Separator = ({
  lineProps,
  ...divProps
}: { lineProps?: DivProps } & DivProps) => {
  return (
    <Div
      display="flex"
      flexDirection="column"
      alignContent="center"
      alignItems="center"
      px={15}
      {...divProps}
    >
      <Div borderBottom="1px solid" width={20} height="50%" {...lineProps} />
    </Div>
  );
};

// TODO move to the comparator-context for a "global" event system
export const sendCustomEvent = (eventName: string, eventPayload: Object) => {
  const customEvent = new CustomEvent(eventName, {
    detail: eventPayload,
  });

  document.dispatchEvent(customEvent);
};

export const parseLegacyResponse = (
  data: any,
  extraFields: FormField[] | undefined = [],
  productType,
): Step[] => {
  if (!data || !Array.isArray(data) || data.length < 1)
    throw new Error('data not found');
  return legacyOrderDefinitionToSteps(data[0], extraFields, productType);
};

type ParsedOrderDefinition = {
  steps: Step[];
  componentDescriptors: ComponentDescriptor[];
  legacyDefinition: LegacyOrderDefinition[];
};

const useOrderDefinition = ({ injectedFields, productType, productField }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error>();
  const [parsedOrderDef, setParsedOrderDef] = useState<ParsedOrderDefinition>();
  const appSteps: Step[] = useSteps() ?? [];
  const product = useMemo(
    () => parseProductFieldData(productField?.value),
    [productField?.value],
  ) as IProduct;
  const { api } = useContext(ComparatorContext);
  if (!productField || !appSteps) return { data: undefined, error, loading };
  if (!product || !product.id || !product?.provider?.id)
    return { data: undefined, error, loading };

  const fetchStepData = useCallback(async () => {
    try {
      const data = await api.getOrderDefinition(product, productType);
      const extraFields = appSteps.flatMap((s) => {
        return s.fields
          ?.filter(
            (f) =>
              injectedFields?.includes(`${s.name}.${f.name}`) ||
              s.name == 'global',
          )
          .map((f) => ({ ...f, active: false }));
      });

      let wizardSteps = parseLegacyResponse(data, extraFields, productType);

      const componentDescriptors = parseLegacyComponents(
        data[0].steps,
        appSteps,
      );

      const wizardStepsWithValues = replaceWithPreviousStepsValues(
        wizardSteps,
        appSteps,
      );

      setParsedOrderDef({
        steps: wizardStepsWithValues,
        legacyDefinition: data,
        componentDescriptors,
      });
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
      localStorage.setItem('lastProductId', product.id);
    }
  }, [product.id, injectedFields, product.provider.id]);

  useEffect(() => {
    fetchStepData();
  }, []);

  return { data: parsedOrderDef, loading, error };
};

const cbMembershipOnBlur = ({ state, wizard }) => {
  let data: object = {};
  state.context.initialSteps.forEach((step) => {
    step.fields.forEach((field) => {
      if (field.name === 'membership_cb_email') {
        data['email'] = field.value;
      }
    });
  });
  wizard.send({
    type: 'SET_GLOBAL',
    fields: [
      {
        name: 'global.isWaitingForCbApiResponse',
        value: 1,
      },
    ],
  });
  if (Object.getOwnPropertyDescriptor(data, 'email')) {
    sendCustomEvent('scw-read-membership', data);
  }
};

interface GetCbTransitionListenerFn {
  (params: {
    wizard: WizardInterpreter | undefined;
    getStep: ReturnType<typeof getStorage>['getStep'];
    setStep: ReturnType<typeof getStorage>['setStep'];
    selectedProductField: FieldHandler;
    wizardStepsField: FieldHandler;
  }): UseWizardOptions['onTransition'];
}

const cbOnBlur = (state, e, wizard) => {
  if (
    e.type == 'xstate.update' &&
    e.state._event.name === 'blur' &&
    e?.state?._event?.data?.target?.name === 'membership_cb_email' &&
    !(
      e.state._event.data.target.name === 'is_cb_member' &&
      e.state._event.data.target.defaultValue === 'ja'
    )
  ) {
    cbMembershipOnBlur({ state, wizard });
  }
};

const cbOnType = (e, wizard) => {
  if (
    e?.type == 'xstate.update' &&
    e?.state?._event?.name === 'CHANGE' &&
    e?.state?._event?.data?.target?.name === 'membership_cb_email'
  ) {
    wizard?.send({
      type: 'SET_GLOBAL',
      fields: [
        {
          name: 'global.isWaitingForCbApiResponse',
          value: 1,
        },
      ],
    });
  }
};

const cbSetIsWaiting = (e, wizard) => {
  if (
    e.type == 'xstate.update' &&
    e.state._event.name === 'blur' &&
    e.state._event.data.target.name === 'is_cb_member' &&
    e.state._event.data.target.defaultValue === 'ja'
  ) {
    wizard?.send({
      type: 'SET_GLOBAL',
      fields: [
        {
          name: 'global.isWaitingForCbApiResponse',
          value: 1,
        },
      ],
    });
  }
};

const getCbTransitionListener: GetCbTransitionListenerFn =
  ({ wizard, getStep, setStep, selectedProductField, wizardStepsField }) =>
  (state, e) => {
    const stepIndex = wizard?.getSnapshot().context.currentStepIndex ?? -1;
    cbOnBlur(state, e, wizard);
    cbOnType(e, wizard);
    cbSetIsWaiting(e, wizard);
    if (e.type.includes('done.invoke')) {
      const currentStep = getStep(stepIndex);
      if (!currentStep) return;
      const newStep = {
        ...currentStep,
        fields: currentStep.fields.map((f) => {
          return f.name === wizardStepsField?.name
            ? { ...f, value: state.context.initialSteps }
            : f;
        }),
      };
      setStep(stepIndex, newStep);
      wizard?.send('RESPAWN');
      if (
        e.type.split('.')[2] ===
        state.context.initialSteps[state.context.initialSteps.length - 1].name
      ) {
        wizard?.send([
          {
            type: 'SET_FORM_FIELDS',
            name: wizardStepsField?.formName,
            fields: [
              {
                name: wizardStepsField?.name,
                value: state.context.initialSteps,
              },
            ],
          },
          'SUBMIT',
        ] as any);
      }
    } else if (e.type === 'xstate.init' || e.type === 'GOTO') {
      sendCustomEvent('wizard-apw-step-change', {
        step: state.context.currentStepIndex,
        selectedProduct: selectedProductField?.value,
        ctx: state.context,
      });
    }
  };

const useCbListener = ({ selectedProductField, wizardStepsField }) => {
  const wizard = useWizardService();
  const { getStep, setStep } = useStorage();
  const cbListener = useMemo(() => {
    return getCbTransitionListener({
      wizard,
      getStep,
      setStep,
      selectedProductField,
      wizardStepsField,
    });
  }, [wizard, getStep, setStep, selectedProductField, wizardStepsField]);
  return { onTransition: cbListener };
};

const useGlobalFieldUpdates = (innerWizard: WizardInterpreter | null) => {
  const fields = useFields([
    '*.isWaitingForCbApiResponse',
    '*.isMember',
    '*.cbLoggedIn',
  ]);
  const [isWaitingResponseField, globalIsMember, cbLoggedIn] = fields;
  useEffect(() => {
    if (innerWizard) {
      const events = innerWizard.getSnapshot().context.initialSteps.map(
        ({ name }) =>
          ({
            type: 'SET_FORM_FIELDS',
            name,
            fields: [
              {
                value: isWaitingResponseField?.value,
                name: 'isWaitingForCbApiResponse',
              },
              {
                value: globalIsMember?.value,
                name: 'isMember',
              },
              {
                value: cbLoggedIn?.value,
                name: 'cbLoggedIn',
              },
            ],
          }) as SetFormFieldsEvent,
      );
      innerWizard.send(events);
    }
  }, [
    innerWizard,
    isWaitingResponseField?.value,
    globalIsMember?.value,
    cbLoggedIn?.value,
  ]);
  return { isWaitingResponseField, globalIsMember, cbLoggedIn };
};

const addGlobalFields = (
  wizardSteps: Step[] = [],
  isWaitingResponseField?: FieldHandler,
) => {
  return wizardSteps.map((s) => {
    return {
      ...s,
      fields: isWaitingResponseField
        ? [
            ...s.fields,
            {
              name: 'shouldBlockUi',
              validator: (v) => (v === '1' ? ['not a member'] : undefined),
              value: '0',
              dependsOn: [
                {
                  conditions: [
                    {
                      name: 'isMember',
                      condition: { fn: 'isNotEqual', args: [1] },
                    },
                    {
                      name: 'is_cb_member',
                      condition: { fn: 'isEqual', args: ['ja'] },
                    },
                  ],
                  value: '1',
                },
                // TODO: the 2 following conditions should not be needed,
                // when the condition above is not fulfilled anymore,
                // the field should switch back to its original value
                {
                  conditions: [
                    {
                      name: 'isMember',
                      condition: { fn: 'isEqual', args: [0] },
                    },
                    {
                      name: 'is_cb_member',
                      condition: { fn: 'isEqual', args: ['nee'] },
                    },
                  ],
                  // TODO: the dependsOn feature does not let us set a falsy value (tested with 0 so far)
                  value: '0',
                },
                {
                  conditions: [
                    {
                      name: 'isMember',
                      condition: { fn: 'isEqual', args: [1] },
                    },
                  ],
                  value: '0',
                },
              ],
            } as FormField,
          ]
        : s.fields,
    };
  });
};

export const ApplyPageWizard = ({
  tabs,
  fieldNames,
  injectedFields = [],
  productType,
  ...tabViewWizardProps
}: ApplyPageWizardProps) => {
  const { name } = useContext(ComparatorContext);

  const wizardRef = useRef<WizardInterpreter | null>(null);
  const [selectedProductField, wizardStepsField] = useFields([
    fieldNames.selectedProduct,
    fieldNames.wizardSteps,
  ]);
  const { data, loading, error } = useOrderDefinition({
    productField: selectedProductField,
    injectedFields,
    productType,
  });
  const { onTransition } = useCbListener({
    selectedProductField,
    wizardStepsField,
  });
  const { isWaitingResponseField } = useGlobalFieldUpdates(wizardRef.current);
  const wizardSteps = useMemo(
    () => addGlobalFields(data?.steps, isWaitingResponseField),
    [JSON.stringify(data?.steps), isWaitingResponseField],
  );

  if (loading)
    return (
      <Div
        display="flex"
        justifyContent={'center'}
        alignItems={'center'}
        my={6}
      >
        <Spinner width={22} height={22} />
      </Div>
    );

  if (error) return <span>{error.message}</span>;
  const ApplyPageComponent =
    name === 'car' ? ApplyPageLayoutCar : ApplyPageLayout;
  const tabsWithJsxContent = wizardSteps.map((s, i) => ({
    label: s.label,
    content: (
      <Div key={'layout-container-' + i}>
        <ApplyPageComponent
          key={'layout-' + i}
          componentDescriptors={data?.componentDescriptors}
          legacyDefinition={data?.legacyDefinition}
          {...tabs[i]?.content.props}
        />
      </Div>
    ),
  }));

  return (
    <TabViewWizard
      wizardCallback={(service) => {
        if (service !== wizardRef.current) wizardRef.current = service;
      }}
      display="flex"
      flexDirection="column"
      tabs={tabsWithJsxContent}
      tabProps={(selected, { disabled }) => ({
        variant: selected ? 'lightTabViewTabSelected' : 'lightTabViewTab',
        css: {
          cursor: selected || disabled ? 'text' : 'pointer',
        },
      })}
      {...tabViewWizardProps}
      wizardOptions={{
        validators,
        transformers,
        onTransition,
      }}
      steps={wizardSteps}
    >
      {(Tabs, Content) => <InnerTabViewWizard Tabs={Tabs} Content={Content} />}
    </TabViewWizard>
  );
};

export interface InnerTabViewWizardProps {
  Tabs: JSX.Element[];
  Content: React.ReactNode;
  variant?: string;
  withTabs?: boolean;
}

export const InnerTabViewWizard: React.FC<InnerTabViewWizardProps> = ({
  Tabs,
  Content,
  variant = 'lightTabViewTabsContainer',
  withTabs = true,
}) => {
  return (
    <Div>
      {withTabs ? (
        <Div display="flex" variant={variant}>
          {Tabs.flatMap((tab, i) => {
            const separator =
              i < Tabs.length - 1 ? (
                <Separator key={'separator-' + i} color="supplementary.6" />
              ) : (
                <Div key={'separator-' + i} />
              );
            return [tab, separator];
          })}
        </Div>
      ) : null}
      <Div>{Content}</Div>
    </Div>
  );
};

export const MembershipWarning = (props) => {
  // Todo move to different project
  const email = useField('*.membership_cb_email');
  const isMember = useField('*.isMember');
  const isCbMember = useField('*.is_cb_member');
  const apiResponse = useField('*.isWaitingForCbApiResponse');

  if (!email || !isMember || !isCbMember || !apiResponse) return null;
  return isCbMember.value === 'ja' &&
    (isMember.value as any) === 0 &&
    apiResponse.value === 0 &&
    email.value ? (
    <Banner
      my={4}
      iconName="alert"
      variant="bannerWarning"
      htmlBody={`<p><strong>Weet je zeker dat je het juiste e-mailadres hebt ingevuld?</strong></p><p>${email.value} is niet verbonden aan een Consumentenbond lidmaatschap. Controleer je e-mailadres en probeer het opnieuw. Mocht je er niet uitkomen, neem dan contact met ons op via 070 - 445 41 41. Op werkdagen zijn we bereikbaar van 9:00 tot 18:00 uur. </p>`}
      {...props}
    />
  ) : null;
};

const InputRow = (props: Partial<RowProps>) => {
  return <Row cols={2} gridColumnGap={6} py={6} {...props} />;
};

const getField = (fields: React.ReactElement[], index: number) => {
  if (index >= fields.length) return null;
  else return fields[index];
};

// temporary name until we figure out what is reusable
export const FormLayout1 = () => {
  const wizard = useWizardService();
  const currentStepIndex = useCurrentStepIndex();
  const fieldNames = useFieldsAttribute('name', currentStepIndex ?? '');
  const formName = useFormAttribute('name', currentStepIndex ?? '');
  const formLabel = useFormAttribute('label', currentStepIndex ?? '');
  if (!wizard || !fieldNames || !formName) return null;
  const stepTitle = useMemo(
    () =>
      formLabel ? (
        <Heading as="h3" variant="H3" mt={0}>
          {formLabel}
        </Heading>
      ) : null,
    [formLabel],
  );
  const fields = useMemo(
    () =>
      fieldNames.map((fieldName) => (
        <Field key={fieldName} fieldName={`${formName}.${fieldName}`} />
      )),
    [formName, fieldNames],
  );
  const restFields = useMemo(() => {
    const _fields: (JSX.Element | null)[] = [];
    for (let x = 5; x < fields.length; x++) {
      _fields.push(<InputRow cols={1}>{getField(fields, x)}</InputRow>);
    }
    return _fields;
  }, [fields]);
  return (
    <>
      {stepTitle}
      <InputRow cols={2}>{getField(fields, 0)}</InputRow>
      <InputRow cols={2}>{getField(fields, 1)}</InputRow>
      <InputRow cols={2}>
        {getField(fields, 2)}
        {getField(fields, 3)}
      </InputRow>
      <hr />
      <InputRow cols={2}>{getField(fields, 4)}</InputRow>
      {restFields}
      <Button variant="primary" onClick={() => wizard.send('NEXT')}>
        Volgende stap
      </Button>
    </>
  );
};
